import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTaxYear } from "../contexts/TaxYearContext";
import TaxYearBadge from "../components/TaxYearBadge";
import { Download, FileText, CheckCircle, Printer, ChevronDown, ChevronRight, AlertTriangle, Calculator, Mail } from "lucide-react";
import ApprovalFilterBanner from "../components/ApprovalFilterBanner";
import ArchiveButton from "../components/ArchiveButton";
import ReportActions from "../components/ReportActions";
import EmailTaxPrepModal from "../components/EmailTaxPrepModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function TurboTaxExport() {
  const { user } = useAuth();
  const { taxYear } = useTaxYear();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [emailOpen, setEmailOpen] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reports/turbotax?tax_year=${taxYear}`, { withCredentials: true });
      setReport(data);
      setGenerated(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generateReport(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [taxYear]);

  const downloadCSV = () => {
    if (!report) return;
    const rows = [
      ["Simple Sign In Tax — TurboTax Export"],
      ["Center:", report.center_name],
      ["Tax Year:", report.tax_year],
      ["Generated:", new Date(report.generated_at).toLocaleString()],
      [""],
      ["=== INCOME ==="],
      ["Category", "Amount"],
      ...report.income.map(i => [i.category, i.amount]),
      ["TOTAL INCOME", report.summary.total_income],
      [""],
      ["=== EXPENSES ==="],
      ["Category", "Amount"],
      ...report.expenses.map(e => [e.category, e.amount]),
      ["TOTAL EXPENSES", report.summary.total_expenses],
      [""],
      ["=== NET INCOME ==="],
      ["Net Profit/Loss", report.summary.net_income],
      [""],
      ["=== TURBOTAX FIELDS (Schedule C) ==="],
      ...Object.entries(report.turbotax_fields || {}).map(([k, v]) => [k.replace(/_/g, " "), v])
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `TurboTax_${report.center_name}_${report.tax_year}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const printReport = () => window.print();

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Tax Prep Document</h1>
            <TaxYearBadge />
          </div>
          <p className="text-slate-500 text-sm mt-1">TurboTax-ready report for {user?.center_name}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={generateReport} disabled={loading}
            className="border border-slate-300 bg-white text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all"
            data-testid="refresh-report-btn">
            {loading ? "Generating..." : "Refresh"}
          </button>
          <button onClick={downloadCSV} disabled={!report}
            className="border border-slate-300 bg-white text-slate-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-all flex items-center gap-2"
            data-testid="download-turbotax-csv">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button
            onClick={() => setEmailOpen(true)}
            disabled={!report}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            data-testid="email-tax-prep-btn"
            title="Email Tax Prep PDF to your accountant"
          >
            <Mail className="w-4 h-4" /> Email to Accountant
          </button>
          <ReportActions
            taxYear={taxYear}
            category="Tax Prep"
            source="/api/reports/turbotax/pdf"
            sourceParams={{}}
            filenameHint={`tax-prep-${(user?.center_name || "center").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").slice(0, 40)}-${taxYear}.pdf`}
            disabled={!report}
            testId="tax-prep"
          />
        </div>
      </div>

      {/* Stage 4 — approved-only filter banner (Tax Prep Document only sums approved txns) */}
      <ApprovalFilterBanner />

      {/* B-3 Duplicate-detection guard — warns when user has BOTH txn rows AND CACFP
          calculator entries for the same category (potential double-count before filing). */}
      {report?.duplicate_warnings?.length > 0 && (
        <div
          data-testid="tax-prep-duplicate-warnings"
          className="bg-red-50 border-2 border-red-300 rounded-xl p-5"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-900" style={{ fontFamily: "Manrope" }}>
                Possible double-count — please review before filing
              </p>
              <p className="text-xs text-red-800 mt-1 leading-relaxed mb-3">
                The following CACFP categories have <strong>both</strong> approved transactions <strong>and</strong> CACFP calculator entries. They will both be included in your Schedule C Line 28 total. If these are the same expenses entered in two places, remove one source.
              </p>
              <div className="space-y-2">
                {report.duplicate_warnings.map((w) => (
                  <div key={w.category} className="bg-white border border-red-200 rounded-lg p-3" data-testid={`dup-warn-${w.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="font-bold text-red-900 text-sm">{w.category}</p>
                    <div className="grid grid-cols-3 gap-2 mt-1.5 text-xs">
                      <div>
                        <p className="text-red-700 font-semibold uppercase tracking-wider">From Transactions</p>
                        <p className="text-slate-800 font-bold">{fmt(w.transactions_amount)}</p>
                      </div>
                      <div>
                        <p className="text-red-700 font-semibold uppercase tracking-wider">From Calculators</p>
                        <p className="text-slate-800 font-bold">{fmt(w.calculator_amount)}</p>
                      </div>
                      <div>
                        <p className="text-red-700 font-semibold uppercase tracking-wider">Combined (will be filed)</p>
                        <p className="text-red-700 font-extrabold">{fmt(w.combined_amount)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* B-3 audit — info banner when calculator entries are merged (no overlap, no warning needed). */}
      {report?.synthetic_total > 0 && !(report?.duplicate_warnings?.length > 0) && (
        <div
          data-testid="tax-prep-synthetic-info"
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3"
        >
          <Calculator className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-900">
              {fmt(report.synthetic_total)} from CACFP calculators is included in your Schedule C totals
            </p>
            <p className="text-xs text-blue-700 mt-1 leading-relaxed">
              We auto-pulled your saved Use Charge, Monthly Ledger, and Food &amp; Labor Tracker entries so this report matches your Dashboard. Source of truth: <strong>CACFP Overview</strong> tabs.
            </p>
          </div>
        </div>
      )}

      {/* What's included */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
        <h3 className="font-semibold text-blue-900 text-sm mb-3" style={{ fontFamily: "Manrope" }}>This report includes:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["Income & Expense Summary", "CACFP Categories", "Transaction Details", "Schedule C Fields"].map(item => (
            <div key={item} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="text-blue-700 text-xs font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {loading && !report && (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <div className="w-10 h-10 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Generating your TurboTax report...</p>
        </div>
      )}

      {report && (
        <div className="space-y-6" data-testid="turbotax-report-content">
          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}>
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-extrabold text-slate-900 text-xl" style={{ fontFamily: "Manrope" }}>{report.center_name}</h2>
                <p className="text-slate-500 text-sm">Tax Year {report.tax_year} | Generated {new Date(report.generated_at).toLocaleString()}</p>
              </div>
            </div>

            {/* Summary Boxes */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
                <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">Total Income</p>
                <p className="text-2xl font-extrabold text-emerald-700 mt-1" style={{ fontFamily: "Manrope" }}>{fmt(report.summary.total_income)}</p>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
                <p className="text-xs text-red-600 font-semibold uppercase tracking-wider">Total Expenses</p>
                <p className="text-2xl font-extrabold text-red-700 mt-1" style={{ fontFamily: "Manrope" }}>{fmt(report.summary.total_expenses)}</p>
              </div>
              <div className={`border rounded-xl p-4 text-center ${report.summary.net_income >= 0 ? "bg-blue-50 border-blue-100" : "bg-orange-50 border-orange-100"}`}>
                <p className={`text-xs font-semibold uppercase tracking-wider ${report.summary.net_income >= 0 ? "text-blue-600" : "text-orange-600"}`}>Net Profit/Loss</p>
                <p className={`text-2xl font-extrabold mt-1 ${report.summary.net_income >= 0 ? "text-blue-700" : "text-orange-700"}`} style={{ fontFamily: "Manrope" }}>{fmt(report.summary.net_income)}</p>
              </div>
            </div>
          </div>

          {/* TurboTax Schedule C Fields */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="schedule-c-fields">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-purple-600" />
              </div>
              <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>TurboTax Schedule C Fields</h2>
              <span className="text-slate-400 text-xs ml-auto">Click any row to see source uploads</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Field</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const panel = report.schedule_c_panel || [];
                  const rows = [];
                  let lastSection = null;
                  const sectionTitles = {
                    totals: "Schedule C — Totals",
                    income: "Income Categories",
                    expense: "Expense Categories",
                    schedule_b: "Schedule B (Pass-through to 1040 — NOT Schedule C)",
                  };
                  panel.forEach((row, i) => {
                    if (row.section !== lastSection) {
                      lastSection = row.section;
                      rows.push(
                        <tr key={`hdr-${row.section}`} className="bg-slate-100 border-y border-slate-200">
                          <td colSpan={2} className="px-6 py-2 text-[10px] font-bold text-slate-600 uppercase tracking-widest">
                            {sectionTitles[row.section] || row.section}
                          </td>
                        </tr>
                      );
                    }
                    const expanded = expandedRows[`${row.section}-${row.label}`];
                    const hasSources = (row.sources || []).length > 0;
                    const valColor = row.section === "income" || row.section === "schedule_b"
                      ? "text-emerald-600"
                      : row.section === "expense"
                        ? "text-red-600"
                        : (row.value >= 0 ? "text-emerald-600" : "text-red-600");
                    rows.push(
                      <tr
                        key={`${row.section}-${i}`}
                        onClick={() => hasSources && setExpandedRows(prev => ({ ...prev, [`${row.section}-${row.label}`]: !prev[`${row.section}-${row.label}`] }))}
                        className={`border-b border-slate-50 ${hasSources ? "hover:bg-slate-50 cursor-pointer" : ""} ${row.is_total ? "bg-slate-50/60 font-bold" : ""}`}
                        data-testid={`schedule-c-row-${row.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      >
                        <td className="px-6 py-3 text-slate-700">
                          <div className="flex items-center gap-2">
                            {hasSources ? (
                              expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                            ) : <span className="w-3.5" />}
                            <span className={row.is_total ? "font-bold" : "font-medium"}>{row.label}</span>
                          </div>
                          {row.note && <p className="text-[11px] text-amber-600 italic mt-0.5 ml-5">{row.note}</p>}
                        </td>
                        <td className={`px-6 py-3 text-right font-semibold whitespace-nowrap ${valColor}`}>{fmt(row.value)}</td>
                      </tr>
                    );
                    if (expanded && hasSources) {
                      rows.push(
                        <tr key={`exp-${row.section}-${i}`} className="bg-slate-50/40 border-b border-slate-100">
                          <td colSpan={2} className="px-6 py-3">
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-5">Sourced from {row.sources.length} upload{row.sources.length !== 1 ? "s" : ""}</p>
                            <div className="ml-5 space-y-1.5">
                              {row.sources.slice(0, 30).map((s, idx) => (
                                <div key={idx} className="flex items-center gap-3 text-xs bg-white border border-slate-200 rounded px-3 py-1.5">
                                  <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  <span className="text-slate-500 w-12 flex-shrink-0">{s.date}</span>
                                  <span className="flex-1 truncate text-slate-700">{s.description}</span>
                                  <span className="text-slate-400 text-[10px] truncate max-w-[200px]">{s.filename}</span>
                                  <span className="text-slate-400 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{s.document_type}</span>
                                  <span className="font-semibold text-slate-900 w-20 text-right">{fmt(s.amount)}</span>
                                </div>
                              ))}
                              {row.sources.length > 30 && (
                                <p className="text-[11px] text-slate-400 italic">+{row.sources.length - 30} more</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  });
                  return rows;
                })()}
              </tbody>
            </table>
          </div>

          {/* Income & Expense Detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="income-detail-table">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Income Detail</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {report.income.length === 0 ? (
                    <tr><td colSpan={2} className="px-6 py-6 text-center text-slate-400 text-xs">No income recorded</td></tr>
                  ) : report.income.map(i => (
                    <tr key={i.category} className="border-b border-slate-50">
                      <td className="px-6 py-2.5 text-slate-600">{i.category}</td>
                      <td className="px-6 py-2.5 text-right text-emerald-600 font-semibold">{fmt(i.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-emerald-50 font-bold">
                    <td className="px-6 py-3 text-slate-900">Total</td>
                    <td className="px-6 py-3 text-right text-emerald-600">{fmt(report.summary.total_income)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="expense-detail-table">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Expense Detail</h2>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {report.expenses.length === 0 ? (
                    <tr><td colSpan={2} className="px-6 py-6 text-center text-slate-400 text-xs">No expenses recorded</td></tr>
                  ) : report.expenses.map(e => (
                    <tr key={e.category} className="border-b border-slate-50">
                      <td className="px-6 py-2.5 text-slate-600">{e.category}</td>
                      <td className="px-6 py-2.5 text-right text-red-600 font-semibold">{fmt(e.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-red-50 font-bold">
                    <td className="px-6 py-3 text-slate-900">Total</td>
                    <td className="px-6 py-3 text-right text-red-600">{fmt(report.summary.total_expenses)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4">
            <p className="text-slate-600 text-sm"><strong>How to use in TurboTax:</strong> Download the CSV file above. In TurboTax, navigate to Business Income {'>'} Schedule C. Use the Schedule C Fields from this report to enter your income and expense amounts. For CACFP-specific deductions, enter under "Other Expenses" with the category name.</p>
          </div>
        </div>
      )}
      <EmailTaxPrepModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        taxYear={taxYear}
        centerName={user?.center_name}
      />
    </div>
  );
}
